/**
 * Trade-report model interfaces and DTOs for the Covered Calls Tracker.
 *
 * Reports are uploaded PDFs stored in S3 with lightweight metadata persisted in
 * DynamoDB. These types are shared across the shared, backend, and frontend
 * packages so the upload/confirm/list/view contract stays type-safe end to end.
 */

/** The type of trade report being uploaded against a position. */
export enum TradeReportLabel {
  ENTRY = 'ENTRY',
  ADDITIONAL_OPTION = 'ADDITIONAL_OPTION',
  EXIT = 'EXIT',
  OTHER = 'OTHER',
}

/** Metadata record for one uploaded trade-report PDF. */
export interface TradeReport {
  tenantId: string; // partition scope; from Cognito claims.sub, never client input
  positionId: string; // owning position
  reportId: string; // uuid generated by backend
  label: TradeReportLabel;
  note?: string; // optional free text
  filename: string; // original client filename (display only)
  contentType: string; // always 'application/pdf' for accepted uploads
  sizeBytes: number; // validated <= MAX_SIZE_BYTES
  s3Key: string; // trade-reports/{tenantId}/{positionId}/{reportId}.pdf (backend-controlled)
  uploadedAt: string; // ISO 8601, set on confirm
}

/** Request body for obtaining a presigned upload URL. */
export interface PresignUploadRequest {
  label: TradeReportLabel;
  note?: string;
  filename: string;
  contentType: string; // expected 'application/pdf'
  sizeBytes: number; // expected <= MAX_SIZE_BYTES
}

/** Response returned when a presigned upload URL is issued. */
export interface PresignUploadResponse {
  reportId: string;
  uploadUrl: string;
  fields?: Record<string, string>; // present for presigned POST
  s3Key: string;
  expiresAt: string;
}

/** Request body for confirming an upload once the browser has sent bytes to S3. */
export interface ConfirmUploadRequest {
  label: TradeReportLabel;
  note?: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/** Response returned when a presigned view (GET) URL is issued. */
export interface ViewUrlResponse {
  viewUrl: string;
  expiresAt: string;
  filename: string;
  contentType: string;
}

/** Response listing all report metadata for a position. */
export interface TradeReportListResponse {
  reports: TradeReport[];
}
